import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { useLocalStorage } from 'usehooks-ts';
import { DateTime } from 'luxon';
import {
  ActionStatus,
  EscalationStatus,
  EscalationTableFields,
} from 'shared';

import { setFilterModel as setContactFilterModel } from '../slices/contactTableModels';
import { setFilterModel as setInvoiceFilterModel } from '../slices/invoiceTableModels';
import { setFilterModel as setEscalationFilterModel, setSortModel as setEscalationSortModel } from '../slices/escalationTableModels';
import { setCurrentTab as setActionTab } from '../slices/actionTableModels';
import { buildInvoiceAgingBucketFilterItems, InvoiceAgingBucket } from '../lib/invoiceAgingBuckets';

import { ContactTableFields } from './useContacts';
import { InvoiceTableFields } from './useInvoices';
import { useGetSelectedOrganisation } from './useGetSelectedOrganisation';
import { useUser } from './useUser';
import { QuickFilterTypes, useActionTableModels } from './useActions';

export const contactAgeRanges = [
  { key: `0-30`, label: `≤ 30 days` },
  { key: `30+`, label: `30+ days` },
  { key: `60+`, label: `60+ days` },
  { key: `90+`, label: `90+ days` },
  { key: `120+`, label: `120+ days` },
] as const;

export const escalationQuickFilters = [
  { key: `due_now`, label: `Due now` },
  { key: `upcoming`, label: `Upcoming` },
  { key: `assigned_to_me`, label: `Assigned to me` },
] as const;

export type EscalationQuickFilterKey = typeof escalationQuickFilters[number][`key`];

// Same presets as the actions table toolbar, each lands on its status tab
export const actionQuickFilters = [
  { key: QuickFilterTypes.pendingAndDue, label: `Scheduled now`, tab: ActionStatus.PENDING },
  { key: QuickFilterTypes.recentlyCompleted, label: `Recently complete`, tab: ActionStatus.COMPLETE },
] as const;

// Shared navigation handlers for the sidebar quick filters (contact age
// ranges + invoice aging buckets). Used by the desktop nested sidebar and the
// mobile sidebar.
export function useQuickFilterNavigation(onNavigate?: () => void) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentOrg = useGetSelectedOrganisation();
  const { data: user } = useUser();
  const [, setInvoiceListView] = useLocalStorage<`table` | `board`>(`invoiceListView`, `table`);
  const { onQuickFilter: onActionQuickFilter } = useActionTableModels();

  function navigateToActionQuickFilter(key: QuickFilterTypes) {
    const preset = actionQuickFilters.find(f => f.key === key);

    if (!preset) return;

    onActionQuickFilter(key);
    dispatch(setActionTab(preset.tab));
    navigate(`/${currentOrg?.id}/actions?persist=true`);
    onNavigate?.();
  }

  function navigateToEscalationQuickFilter(key: EscalationQuickFilterKey) {
    const pendingItem = {
      field: EscalationTableFields.Status,
      operator: `is`,
      value: EscalationStatus.PENDING,
      id: Math.round(Math.random() * 1e5),
    };

    let items;

    switch (key) {
    case `due_now`:
      items = [
        pendingItem,
        {
          field: EscalationTableFields.ScheduledDate,
          operator: `onOrBefore`,
          value: DateTime.now().endOf(`day`).toISO(),
          id: Math.round(Math.random() * 1e5),
        },
      ];
      break;
    case `upcoming`:
      items = [
        pendingItem,
        {
          field: EscalationTableFields.ScheduledDate,
          operator: `onOrAfter`,
          value: DateTime.now().plus({ days: 1 }).startOf(`day`).toISO(),
          id: Math.round(Math.random() * 1e5),
        },
      ];
      break;
    case `assigned_to_me`:
      if (!user?.id) return;

      items = [
        pendingItem,
        {
          field: EscalationTableFields.Assignee,
          operator: `is`,
          value: user.id,
          id: Math.round(Math.random() * 1e5),
        },
      ];
      break;
    default:
      return;
    }

    dispatch(setEscalationFilterModel({ items }));

    dispatch(setEscalationSortModel([
      {
        field: EscalationTableFields.ScheduledDate,
        sort: `asc`,
      },
    ]));

    navigate(`/${currentOrg?.id}/escalations?persist=true`);
    onNavigate?.();
  }

  function navigateToInvoiceAgingBucket(bucket: InvoiceAgingBucket) {
    dispatch(setInvoiceFilterModel({
      items: buildInvoiceAgingBucketFilterItems(bucket),
    }));

    // Buckets prefilter the list view, so make sure we land on it
    setInvoiceListView(`table`);
    navigate(`/${currentOrg?.id}/invoices?persist=true`);
    onNavigate?.();
  }

  function navigateWithAgeFilter(pageType: `contacts` | `invoices`, ageRange: string) {
    const fieldName = pageType === `contacts` ? ContactTableFields.oldestDaysOverdue : InvoiceTableFields.daysOverdue;
    const setFilterModel = pageType === `contacts` ? setContactFilterModel : setInvoiceFilterModel;
    const url = pageType === `contacts` ? `/${currentOrg?.id}/contacts/manage` : `/${currentOrg?.id}/invoices`;

    switch (ageRange) {
    case `0-30`:
      dispatch(setFilterModel({
        items: [
          {
            field: fieldName,
            operator: `<=`,
            value: 30,
            id: Math.round(Math.random() * 1e5),
          },
        ],
      }));
      break;
    case `30+`:
      dispatch(setFilterModel({
        items: [
          {
            field: fieldName,
            operator: `>=`,
            value: 30,
            id: Math.round(Math.random() * 1e5),
          },
        ],
      }));
      break;
    case `60+`:
      dispatch(setFilterModel({
        items: [
          {
            field: fieldName,
            operator: `>=`,
            value: 60,
            id: Math.round(Math.random() * 1e5),
          },
        ],
      }));
      break;
    case `90+`:
      dispatch(setFilterModel({
        items: [
          {
            field: fieldName,
            operator: `>=`,
            value: 90,
            id: Math.round(Math.random() * 1e5),
          },
        ],
      }));
      break;
    case `120+`:
      dispatch(setFilterModel({
        items: [
          {
            field: fieldName,
            operator: `>=`,
            value: 120,
            id: Math.round(Math.random() * 1e5),
          },
        ],
      }));
      break;
    default:
      return;
    }

    navigate(url);
    onNavigate?.();
  }

  return {
    navigateToActionQuickFilter,
    navigateToInvoiceAgingBucket,
    navigateWithAgeFilter,
    navigateToEscalationQuickFilter,
  };
}
